import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ZoomInIcon, XIcon } from 'lucide-react';
export function TransformerTheatre() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  return <div className="w-full min-h-screen bg-white" data-id="element-1517">
      {/* Image Modal */}
      {selectedImage && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setSelectedImage(null)} data-id="element-1518">
          <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-white hover:text-emerald-400 transition-colors" data-id="element-1519">
            <XIcon className="w-8 h-8" data-id="element-1520" />
          </button>
          <motion.img initial={{
        scale: 0.9
      }} animate={{
        scale: 1
      }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain" data-id="element-1521" />
        </motion.div>}
      {/* Header */}
      <header className="py-8 px-6 bg-white border-b border-stone-200 sticky top-0 z-40" data-id="element-1522">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-1523">
          <Link to="/architecture" className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-id="element-1524">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-1525" />
            <span className="font-light" data-id="element-1526">Back to Architecture</span>
          </Link>
          <h1 className="text-2xl font-light text-stone-900" data-id="element-1527">Maria Elina</h1>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 via-emerald-50/30 to-stone-50" data-id="element-1528">
        <div className="max-w-5xl mx-auto" data-id="element-1529">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-1530">
            <p className="text-emerald-600 font-light tracking-wide uppercase mb-4" data-id="element-1531">
              Interior Architecture, Adaptive Reuse
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-stone-900 mb-6" data-id="element-1532">
              Transformer Theatre
            </h1>
            <p className="text-xl text-stone-600 font-light mb-8" data-id="element-1533">
              A renovation proposition for an abandoned Art Deco building in
              Bournemouth, reimagined as a flexible, multi-configuration
              performance space
            </p>
            <div className="space-y-4 text-stone-700 font-light" data-id="element-1534">
              <p className="text-lg" data-id="element-1535">
                <span className="font-normal text-stone-900" data-id="element-1536">Role:</span>{' '}
                Interior Architecture, Spatial Design
              </p>
              <p className="text-lg" data-id="element-1537">
                <span className="font-normal text-stone-900" data-id="element-1538">Location:</span>{' '}
                Bournemouth, UK
              </p>
              <p className="text-lg" data-id="element-1539">
                <span className="font-normal text-stone-900" data-id="element-1540">Type:</span>{' '}
                Academic Project, Adaptive Reuse
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Project Overview */}
      <section className="py-20 px-6 bg-white" data-id="element-1541">
        <div className="max-w-5xl mx-auto" data-id="element-1542">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1543">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-1544">
              Project Overview
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-1545">
              The Transformer Theatre project reimagines an abandoned Art Deco
              building in Bournemouth as a dynamic, adaptable performance venue.
              The design responds to the evolving needs of contemporary theatre
              and entertainment spaces, creating a facility that can seamlessly
              transitions between multiple configurations to accommodate
              different event types and audience sizes.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-1546">
              Rather than imposing a single-use program on the historic
              structure, the design embraces flexibility as its core principle.
              The "transformer" concept allows the space to adapt from
              traditional proscenium theatre, thrust stage and experimental
              performance configurations to event and gallery venue, maximizing
              the building's utility and cultural relevance.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed" data-id="element-1547">
              The project carefully balances preservation of the building's Art
              Deco character with contemporary entertainment requirements,
              creating a dialogue between historic architecture and modern
              lifestyle.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Design Concept - Radial Diagram */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1548">
        <div className="max-w-7xl mx-auto" data-id="element-1549">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1550">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1551">
              Conceptual Framework
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1552">
              The design concept is visualized through this radial diagram,
              which displays the main stage and strongly references the Art Deco
              heritage of the building while incorporating modern technologies.
              The design intends the usage of many timber-imitating elements
              along with metal and textile, to reference the time period's
              fashion for opulance.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/initial%20sketch1.png')} data-id="element-1553">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1554">
                <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/initial%20sketch1.png" alt="Conceptual Radial Diagram" className="w-full h-auto" data-id="element-1555" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1556">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1557" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1558">
                Radial concept sketch illustrating spatial zones, materials and
                aesthetic of the interior
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Interior 3D Rendering */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1559">
        <div className="max-w-7xl mx-auto" data-id="element-1560">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1561">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1562">
              Interior 3D Rendering
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1563">
              The primary configuration of above-ground floors features
              traditional theatre seating arranged in a proscenium format, with
              tiered rows providing optimal sightlines. The basement floor
              configuration demonstrates the space's adaptability, with modular
              staging that can be rearranged to create experimental performance
              layouts.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1564">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(28).png')} data-id="element-1565">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1566">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(28).png" alt="Traditional Theatre Configuration" className="w-full h-auto" data-id="element-1567" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1568">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1569" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1570">
                  Isometric view showing traditional proscenium theatre layout
                  with support spaces
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(29).png')} data-id="element-1571">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1572">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(29).png" alt="Flexible Performance Configuration" className="w-full h-auto padding-20px" data-id="element-1573" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1574">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1575" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1576">
                  Basement configuration showing adaptable performance space and
                  gallery
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Conceptual Visual Collage */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1577">
        <div className="max-w-7xl mx-auto" data-id="element-1578">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1579">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1580">
              Art Deco Meets Contemporary Performance
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1581">
              This conceptual collage captures the essence of the
              transformation—where historic Art Deco grandeur meets cutting-edge
              performance technology. The layered imagery suggests the
              building's ability to accommodate multiple simultaneous
              experiences, from intimate gatherings to large-scale projections,
              all within the preserved architectural framework.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/Generated%20Image%20October%2026,%202025%20-%2011_33AM.png')} data-id="element-1582">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1583">
                <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/Generated%20Image%20October%2026,%202025%20-%2011_33AM.png" alt="Conceptual Visual Collage" className="w-full h-auto" data-id="element-1584" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1585">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1586" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1587">
                Conceptual collage illustrating the fusion of Art Deco
                architecture with contemporary performance technology and
                dynamic spatial experiences
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Extended Time-Based Usage - Evening & Night */}
      <section className="py-20 px-6 bg-white" data-id="element-1588">
        <div className="max-w-7xl mx-auto" data-id="element-1589">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1590">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1591">
              24-Hour Programming Strategy
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1592">
              The building's adaptability extends throughout the day,
              transforming from daytime activities into evening performance and
              entertainment. This extended programming maximizes the building's
              utility and creates a vibrant cultural hub that serves the
              community.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1593">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/8%20am%20-%2012%20am.png')} data-id="element-1594">
                <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1595">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/8%20am%20-%2012%20am.png" alt="Daytime Configurations" className="w-full h-auto" data-id="element-1596" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1597">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1598" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-1599">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-1600">
                    Daytime Configurations
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1601">
                    Multi-floor usage showing entry spaces, dining areas, staff
                    facilities, workshops, play areas, and technical preparation
                    zones during daytime hours
                  </p>
                </div>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/6%20pm%20-%2010%20pm.png')} data-id="element-1602">
                <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1603">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/6%20pm%20-%2010%20pm.png" alt="Evening Configurations" className="w-full h-auto" data-id="element-1604" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1605">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1606" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-1607">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-1608">
                    Evening Configurations
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1609">
                    Prime hours with main auditorium, dining areas,
                    bar facilities, and automated dynamic walls facilitating
                    rapid spatial transformation for evening entertainment
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Architectural Structure */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1610">
        <div className="max-w-7xl mx-auto" data-id="element-1611">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1612">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1613">
              Architectural Structure & Layers
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1614">
              This exploded axonometric reveals the complex spatial organization
              and structural hierarchy of the intervention. The design carefully
              integrates new elements within the existing Art Deco shell,
              creating distinct zones for performance, audience, and support
              functions while maintaining visual and spatial connections
              throughout.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(31).png')} data-id="element-1615">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1616">
                <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(31).png" alt="Architectural Structure" className="w-full h-auto" data-id="element-1617" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1618">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1619" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1620">
                3D model section showing architectural layers and structural
                organization
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Functional Zoning & Spatial Organization - Side by Side */}
      <section className="py-20 px-6 bg-white" data-id="element-1621">
        <div className="max-w-7xl mx-auto" data-id="element-1622">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1623">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1624">
              Functional Zoning & Spatial Organization
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1625">
              These diagrams illustrate the careful separation and integration
              of different user groups, functions, and programmatic elements.
              The color-coded analysis shows how operational efficiency is
              optimized through strategic spatial organization.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1626">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(32).png')} data-id="element-1627">
                <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1628">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(32).png" alt="Functional Zoning Above Ground" className="w-full h-auto" data-id="element-1629" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1630">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1631" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-1632">
                  <h3 className="text-lg font-light text-stone-900 mb-2" data-id="element-1633">
                    Functional Zoning Above Ground
                  </h3>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1634">
                    Color-coded diagram showing user group separation and
                    spatial relationships
                  </p>
                </div>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(33).png')} data-id="element-1635">
                <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1636">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/image%20(33).png" alt="Functional Zoning Under Ground" className="w-full h-auto" data-id="element-1637" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1638">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1639" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-1640">
                  <h3 className="text-lg font-light text-stone-900 mb-2" data-id="element-1641">
                    Functional Zoning Under Ground
                  </h3>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1642">
                    Detailed program breakdown showing all functional spaces and
                    their interconnections
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Detailed Intervention Strategy */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1643">
        <div className="max-w-7xl mx-auto" data-id="element-1644">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1645">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1646">
              Detailed Intervention Strategy
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1647">
              The renovation strategy is articulated through detailed sections
              and floor plans that identify specific intervention opportunities.
              Each potential modification is catalogued and evaluated for its
              impact on flexibility, accessibility, and user experience—from
              structural adaptations to service upgrades and spatial
              reconfigurations.
            </p>
            {/* Building Sections with Opportunities */}
            <div className="mb-16" data-id="element-1648">
              <h3 className="text-2xl font-light text-stone-900 mb-6" data-id="element-1649">
                Building Sections: Intervention Opportunities
              </h3>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer mb-8" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/building%20study%20SECTIONS%20(1).png')} data-id="element-1650">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1651">
                  <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/building%20study%20SECTIONS%20(1).png" alt="Building Sections with Intervention Opportunities" className="w-full h-auto" data-id="element-1652" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1653">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1654" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1655">
                  Sectional analysis identifying potential opportunities for
                  functional improvements
                </p>
              </motion.div>
            </div>
            {/* Floor Plans with Opportunities - Primary Set */}
            <div className="mb-16" data-id="element-1656">
              <h3 className="text-2xl font-light text-stone-900 mb-6" data-id="element-1657">
                Floor Plans: Spatial Intervention Catalog
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1658">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/3.png')} data-id="element-1659">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1660">
                    <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/3.png" alt="Ground Floor Intervention Plan" className="w-full h-auto" data-id="element-1661" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1662">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1663" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-1664">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-1665">
                      Ground Floor Interventions
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-1666">
                      Detailed catalog of potential modifications including
                      spatial reconfigurations
                    </p>
                  </div>
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.1
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/2.png')} data-id="element-1667">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1668">
                    <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/2.png" alt="Upper Floor Intervention Plan" className="w-full h-auto" data-id="element-1669" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1670">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1671" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-1672">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-1673">
                      Upper Floor Interventions
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-1674">
                      Strategic modifications for backstage areas, technical
                      spaces, and support facilities to enhance operational
                      efficiency
                    </p>
                  </div>
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.2
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/1.png')} data-id="element-1675">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1676">
                    <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/1.png" alt="Backstage Level Intervention Plan" className="w-full h-auto" data-id="element-1677" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1678">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1679" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-1680">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-1681">
                      Backstage Level Interventions
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-1682">
                      Performer facilities, green rooms, and technical support
                      spaces
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
            {/* Time-Based Usage Diagrams */}
          </motion.div>
        </div>
      </section>
      {/* Site Analysis & Environmental Context */}
      <section className="py-20 px-6 bg-white" data-id="element-1683">
        <div className="max-w-7xl mx-auto" data-id="element-1684">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1685">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1686">
              Site Analysis & Environmental Context
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1687">
              The site's environmental conditions were considered in the design
              strategy. This solar analysis maps sun and wind angles throughout
              the year, informing decisions about the relationship between
              interior spaces and seasonal variations.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/aub_ready/3d%20weather.png')} data-id="element-1688">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1689">
                <img src="https://storage.yandexcloud.net/maria-images/images/aub_ready/3d%20weather.png" alt="Site Solar Analysis" className="w-full h-auto" data-id="element-1690" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1691">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1692" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1693">
                Solar and wind map analysis with massing showing variables
                across different time
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Design Approach */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1694">
        <div className="max-w-5xl mx-auto" data-id="element-1695">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1696">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-1697">
              Design Philosophy & Approach
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed" data-id="element-1698">
              <p data-id="element-1699">
                The Transformer Theatre project emerged from a fundamental
                question: how can historic architecture accommodate the evolving
                nature of contemporary performance? Rather than treating the Art
                Deco building as a fixed container, the design embraces
                flexibility as both a practical necessity and a conceptual
                driver.
              </p>
              <p data-id="element-1700">
                The "transformer" concept operates on multiple levels. At the
                most literal, modular seating systems and adaptable stage
                configurations allow the space to physically transform between
                different theatrical formats. But more profoundly, the design
                transforms the relationship between building and program,
                creating a venue that can evolve with changing cultural needs
                rather than becoming obsolete.
              </p>
              <p data-id="element-1701">
                The preservation strategy respects the building's Art Deco
                heritage while introducing contemporary interventions that are
                clearly legible as new. Ultimately, the project demonstrates how
                adaptive reuse can breathe new life into abandoned buildings,
                transforming them into vibrant cultural assets that serve their
                communities in multiple, evolving ways.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}